import { createSelector } from '@reduxjs/toolkit';

import type {
  FilesFormatSchemaWithId,
  FilesProxySchemaWithId,
} from '@iconik-io/iconik-api/src/services/files';
import type { AssetEntityNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/asset';
import type {
  SearchItemEntity,
  SearchItemEntityNormalized,
} from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import {
  createEntitiesDenormalizer,
  createEntityDenormalizer,
} from '@iconik-io/shared-lib/src/redux/entities/normalizers';
import { selectAssetOrSearchItem } from '@iconik-io/shared-lib/src/redux/entities/selectors/assets';
import { selectFilesByAssetId } from '@iconik-io/shared-lib/src/redux/entities/selectors/files';
import { selectFormatsByAssetId } from '@iconik-io/shared-lib/src/redux/entities/selectors/formats';
import { selectProxiesByAssetId } from '@iconik-io/shared-lib/src/redux/entities/selectors/proxies';
import { selectCurrentVersionId as selectVersionIdForAsset } from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';
import type { Entities } from '@iconik-io/shared-lib/src/redux/entities/types';
import { findClosedProxies } from '@iconik-io/shared-lib/src/utils/find_closed_proxy';
import {
  findLatestPlayableProxy,
  findPlayableProxies,
} from '@iconik-io/shared-lib/src/utils/find_playable_proxy';
import { getProxyMediaType } from '@iconik-io/shared-lib/src/utils/get_proxy_media_type';
import type { TimecodeOptions } from '@iconik-io/shared-lib/src/utils/get_proxy_timecode_options';
import { isLiveAsset } from '@iconik-io/shared-lib/src/utils/is_live_asset';
import { seekableMediaTypes } from '@iconik-io/shared-lib/src/utils/media_type';

import { isCloudStorage } from '../../../components/admin_storage_form_fields/utils';
import { FAKE_SUBCLIP_SEGMENT_ID } from '../../../constants/data';
import {
  ASSET_PAGE_ROUTE,
  ASSET_VERSION_PAGE_ROUTE,
} from '../../../constants/routes';
import { selectCurrentVersionId } from '../../../ducks/version/selectors';
import type { AppState } from '../../../store/types';
import { assetHasSubclipSupport } from '../../../utils/media_type';
import { denormalizeSearchItem } from '../../search/normalizers';

const denormalizeSearchItems = createEntitiesDenormalizer('search_items');

const denormalizeAssets = createEntitiesDenormalizer('assets');

const getIsItemSubclip = (
  entities:
    | Entities<SearchItemEntityNormalized>
    | Entities<AssetEntityNormalized>,
  id: string,
  assetId?: string,
  versionId?: string,
) =>
  entities[id].type === 'SUBCLIP' &&
  entities[id].original_asset_id === assetId &&
  entities[id].original_version_id === versionId;

const getEntities = (state: AppState) => state.entities;

const getSearchItems = (state: AppState) => state.entities.search_items;
const getAssets = (state: AppState) => state.entities.assets;

const getAssetId = (_state: AppState, assetId: string | undefined) => assetId;

export const selectSearchItem = createSelector(
  getEntities,
  getAssetId,
  (entities, assetId): SearchItemEntity | undefined =>
    assetId ? denormalizeSearchItem(entities, assetId) : undefined,
);

export const selectSubclipAssets = createSelector(
  getSearchItems,
  getAssets,
  getEntities,
  selectCurrentVersionId,
  getAssetId,
  (searchItems, assets, entities, versionId, assetId) => {
    const assetIds = Object.keys(assets).filter((id) =>
      getIsItemSubclip(assets, id, assetId, versionId),
    );

    const searchItemIds = Object.keys(searchItems).filter(
      (id) =>
        !assetIds.includes(id) &&
        getIsItemSubclip(searchItems, id, assetId, versionId),
    );

    const assetValues = denormalizeAssets(entities, assetIds);
    const searchItemsValues = denormalizeSearchItems(entities, searchItemIds);
    const items = [...assetValues, ...searchItemsValues];
    return items;
  },
);

// Selector to convert subclip search items to segments
// to be able to show subclips markers on timeline of the videoplayer
export const selectFakeSubclipSegments = createSelector(
  selectSubclipAssets,
  getAssetId,
  (searchItems, assetId) => {
    const segments: SegmentEntityWithIdNormalized[] = [];
    if (!assetId) {
      return segments;
    }
    for (const item of searchItems) {
      segments.push({
        id: `${FAKE_SUBCLIP_SEGMENT_ID}${item.id}`,
        asset_id: assetId,
        segment_type: 'MARKER',
        time_end_milliseconds: item.time_end_milliseconds,
        time_start_milliseconds: item.time_start_milliseconds,
      });
    }
    return segments;
  },
);

const denormalizeAsset = createEntityDenormalizer('assets');

export const selectAsset = createSelector(
  getEntities,
  getAssetId,
  (entities, assetId) =>
    assetId ? denormalizeAsset(entities, assetId) : undefined,
);

type ProxyVersionScope = {
  type?: string | null;
  original_asset_id?: string | null;
  original_version_id?: string | null;
};

const filterProxiesForAssetAndVersion = (
  proxies: FilesProxySchemaWithId[],
  asset: ProxyVersionScope | undefined,
  versionId: string | undefined,
) => {
  if (asset?.type === 'SUBCLIP') {
    const nonParentProxies = proxies.filter((proxy) =>
      versionId ? proxy.version_id === versionId : true,
    );

    if (nonParentProxies.length > 0) {
      return nonParentProxies;
    }
    const parentProxies = proxies.filter(
      (proxy) =>
        proxy.asset_id === asset.original_asset_id &&
        proxy.version_id === asset.original_version_id,
    );
    return parentProxies;
  }
  return proxies.filter((proxy) =>
    versionId ? proxy.version_id === versionId : true,
  );
};

export const selectAssetProxies = createSelector(
  selectProxiesByAssetId,
  selectCurrentVersionId,
  selectAsset,
  (proxies, versionId, asset) =>
    filterProxiesForAssetAndVersion(proxies, asset, versionId),
);

export const selectClosedAssetProxies = createSelector(
  selectAssetProxies,
  (proxies) => findClosedProxies(proxies),
);

export const selectPlayableAssetProxies = createSelector(
  selectAssetProxies,
  (proxies) => findPlayableProxies(proxies),
);

// The route-scoped version above only resolves on the asset route, so callers
// elsewhere (Portals) end up with proxies from every version. This variant reads
// the version from the selector's own arguments instead, falling back to the
// asset's latest active version.
const selectAssetProxiesForVersion = createSelector(
  selectProxiesByAssetId,
  selectVersionIdForAsset,
  selectAssetOrSearchItem,
  (proxies, versionId, asset) =>
    filterProxiesForAssetAndVersion(proxies, asset, versionId),
);

export const selectPlayableAssetProxiesForVersion = createSelector(
  selectAssetProxiesForVersion,
  (proxies) => findPlayableProxies(proxies),
);

export const selectIsLiveIngest = createSelector(
  selectAssetProxiesForVersion,
  selectFilesByAssetId,
  (proxies, files) => isLiveAsset({ proxies, files }),
);

export const selectAssetProxy = createSelector(
  selectAssetProxies,
  (proxies): FilesProxySchemaWithId | undefined =>
    findLatestPlayableProxy(proxies),
);

export const selectAssetProxyTimecodeOptions = createSelector(
  selectProxiesByAssetId,
  selectAsset,
  selectCurrentVersionId,
  (proxies, asset, versionId): TimecodeOptions | undefined => {
    const items = filterProxiesForAssetAndVersion(proxies, asset, versionId);
    const assetProxy = findLatestPlayableProxy(items);
    if (!assetProxy) {
      return undefined;
    }
    const startTimecode = assetProxy.start_time_code ?? undefined;
    const frameRate = Number(assetProxy.frame_rate ?? 25);
    const isDropFrame = Boolean(assetProxy.is_drop_frame);
    return {
      startTimecode,
      frameRate,
      isDropFrame,
    };
  },
);

export const selectAssetFiles = createSelector(
  selectFilesByAssetId,
  getAssetId,
  selectCurrentVersionId,
  (files, assetId, versionId) => {
    if (!assetId) {
      return [];
    }
    const cachedUrls: Record<string, string> = {};
    const assetFiles = files.filter((file) =>
      versionId ? file.version_id === versionId : true,
    );
    assetFiles.forEach((file) => {
      if (file.url && !file.url.includes('NEED_TO_GET_AUTH')) {
        cachedUrls[file.id] = file.url;
      }
    });
    const assetFilesWithUrls = assetFiles.map((file) => {
      if (
        file.url &&
        file.url.includes('NEED_TO_GET_AUTH') &&
        cachedUrls[file.id]
      ) {
        return Object.assign(file, {
          url: cachedUrls[file.id],
        });
      }
      return file;
    });

    return assetFilesWithUrls;
  },
);

export const selectAssetCloudFiles = createSelector(
  selectAssetFiles,
  getEntities,
  (files, entities) =>
    files.filter((file) => {
      const storage = file.storage_id
        ? entities.storages[file.storage_id]
        : undefined;
      return (
        file.status === 'CLOSED' &&
        isCloudStorage(storage) &&
        storage?.purpose !== 'ARCHIVE'
      );
    }),
);

export const selectAssetFormats = createSelector(
  selectFormatsByAssetId,
  selectCurrentVersionId,
  (formats, versionId) =>
    formats.filter((format) => {
      let result = format.status !== 'DELETED';
      if (versionId) {
        result &&= format.version_id === versionId;
      }

      return result;
    }),
);

export const selectAssetOriginalFormats = createSelector(
  selectAssetFormats,
  (formats) =>
    formats.filter((format) => format.name?.toUpperCase() === 'ORIGINAL'),
);

export const selectAssetOriginalFormat = createSelector(
  selectAssetOriginalFormats,
  (formats): FilesFormatSchemaWithId | undefined => formats[0],
);

export const selectAssetOriginalFormatFirstMetadata = createSelector(
  selectAssetOriginalFormat,
  (format) => format?.metadata?.[0],
);

export const selectAssetOriginalFiles = createSelector(
  selectAssetOriginalFormat,
  selectFilesByAssetId,
  (originalFormat, files) => {
    return files.filter((file) => file.format_id === originalFormat?.id);
  },
);

export const selectIsTimeBasedAsset = createSelector(
  selectAssetProxy,
  selectAsset,
  (assetProxy, asset) => {
    if (!assetProxy || !asset) {
      return false;
    }
    const seekable = seekableMediaTypes.has(getProxyMediaType(assetProxy));
    return asset.type === 'PLACEHOLDER' || seekable;
  },
);

export const selectAllowSubclips = createSelector(
  selectAssetProxy,
  selectAsset,
  (assetProxy, asset) => {
    if (!assetProxy || !asset) {
      return false;
    }
    return assetHasSubclipSupport(getProxyMediaType(assetProxy), asset.type);
  },
);

const getParamRoute = (state: AppState) => state.routeParams.route;

export const selectIsAssetPage = createSelector(getParamRoute, (route) => {
  return route === ASSET_PAGE_ROUTE || route === ASSET_VERSION_PAGE_ROUTE;
});
